import { type GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'

import { signupPageModuleQuery } from '@data/sanity/queries/page'
import { type SanitySignupPage } from '@data/sanity/queries/types/page'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getNonNullValues } from '@lib/helpers'
import {
  type BasePageProps,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { type CustomStaticPropsContext } from '@lib/routes'
import { defaultModuleQueryParameters, getSignupPage } from '@lib/sanity/page'
import { getVideoObjectSchemas } from '@lib/schema/video'

import Layout from '@components/layout'
import Modules from '@modules/modules'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type SignupPageProps = BasePageProps & {
  page: SanitySignupPage
  site: SanitySiteFragment
}

const SignupPage = ({ draftMode, locale, page, site }: SignupPageProps) => {
  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [...videoObjectSchemas]

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={signupPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext
): Promise<GetStaticPropsResult<SignupPageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const signupPage = await getSignupPage(sanityClient, context.locale)

  if (!signupPage.page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      page: getNonNullValues(signupPage.page),
      site: getNonNullValues(signupPage.site),
    },
    revalidate: 60,
  }
}

export default SignupPage
